import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  subscribeMeetingIntelEvents,
  type MeetingIntelEvent,
} from "../meetingIntelligenceRealtime";
import type {
  MappingReason,
  MeetingMappingRow,
  MeetingMappingAssignPayload,
  MeetingMappingInternalPayload,
} from "../meetingIntelligence.types";
import type { ClientRecord } from "../../clients/clients.types";
import {
  MAPPING_QUEUE_SERVER_CAP,
  rowsReturned,
  isAtCap,
  listCaption,
  type LoadCap,
} from "shared/truncation";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { swallowedFailure } from "../../devops/shared/swallowedFind";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * Reduce a ServiceStore `find()` response to its rows. `meeting-mappings`
 * answers with a bare array; a `{ data }` envelope is tolerated so a future
 * pagination switch cannot silently read as zero rows. `null`/`undefined` is
 * NOT handled here — the caller must treat it as the swallowed failure it is.
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/** A partner option for the (optional) external-parties picker. */
export interface PartnerOption {
  id: string;
  name: string;
}

/** The operator's in-progress triage of one queued meeting. */
interface Selection {
  clientId: string;
  partnerIds: string[];
}

/**
 * Human-readable explanation of every `mappingReason` the API can return — WHY
 * a meeting landed in the queue, which is the whole point of the queue now: the
 * operator is answering "who is this meeting with?", not "which project?".
 */
export const MAPPING_REASON_META: Record<
  MappingReason,
  { label: string; help: string; color: "warning" | "info" | "error" | "default" }
> = {
  no_known_domain: {
    label: "No known domain",
    help: "No attendee's email domain matches any client or partner we know.",
    color: "warning",
  },
  multiple_clients: {
    label: "Multiple clients",
    help: "Attendees span more than one client — pick the client this meeting is actually for.",
    color: "info",
  },
  partner_only: {
    label: "Partner only",
    help: "Only partner (external, non-client) domains attended — pick the client it concerns.",
    color: "info",
  },
  internal_only: {
    label: "Internal only",
    help: "Only IntraSync attendees — pick the client it concerns, or mark it Internal to resolve it without one.",
    color: "default",
  },
  domain_conflict: {
    label: "Domain conflict",
    help: "An attendee domain is claimed by more than one party — resolve it by picking the client.",
    color: "error",
  },
  unmarked_internal: {
    label: "Sent back from Internal",
    help: "Someone marked this meeting internal, then sent it back from Internal Meetings — pick the client it was with.",
    color: "info",
  },
};

/** The `mappingReason` keys, in the order the "Why" filter lists them. */
export const MAPPING_REASON_KEYS = Object.keys(
  MAPPING_REASON_META
) as MappingReason[];

/** Header "Why" facet: one `mappingReason`, or "all". */
export type MappingReasonFilter = "all" | MappingReason;

/** Columns the queue table sorts by. Client and Assign hold the operator's
 *  in-progress inputs and stay plain, exactly as Meetings leaves Status and
 *  Actions unsorted. */
export type MappingQueueSortKey =
  | "title"
  | "ownerName"
  | "reason"
  | "startTime"
  | "createdAt";

export type MappingQueueSortDir = SortDir;

/** Sort keys that read best newest-first on the first click. */
const DESC_FIRST: ReadonlySet<MappingQueueSortKey> = new Set([
  "startTime",
  "createdAt",
]);

/** An owner present in the loaded queue — the Owner filter's options. */
export interface MappingQueueOwnerOption {
  id: string;
  name: string;
}

/**
 * Identity of a row's owner for the Owner facet: the email (the stable one),
 * falling back to the display name, and "" when the API sent neither. A row
 * with no owner is not offered as an option and never matches a chosen one.
 */
const ownerKey = (row: MeetingMappingRow): string =>
  (row.ownerEmail || row.ownerName || "").trim().toLowerCase();

/**
 * View store for the "Mapping Queue" — meetings that could not be auto-mapped to
 * a CLIENT. Projects are gone: a meeting is now held WITH a client (optionally
 * alongside external partners), so the operator picks a client and the API
 * assigns + reprocesses the meeting.
 *
 * Each queued row carries a `mappingReason` explaining why it is here
 * (`no_known_domain` | `multiple_clients` | `partner_only` | `internal_only` |
 * `domain_conflict`), which the view surfaces so the operator can triage the row
 * without opening the meeting.
 */
class MappingQueueStore {
  rootStore: RootStore;
  loading = false;
  error: string | null = null;
  /**
   * Whether the QUEUE read itself failed, as opposed to anything else that can
   * fill `error` (a failed picker load, a refused write). Only this may turn
   * an empty table into "we could not read the queue" — a failed clients load
   * says nothing about what is waiting, and a write's refusal says nothing
   * about it either.
   */
  private queueLoadFailed = false;
  assigningId: number | null = null;
  selections: Record<number, Selection> = {};

  // ---- Header facets ------------------------------------------------------
  // ALL client-side, and that is not a preference: `meeting-mappings.find`
  // takes no query at all — it hardcodes `mappingStatus='needs_review'`,
  // `orderBy createdAt desc` and `.limit(200)`, so a $sort/$limit/field filter
  // would be silently discarded. Every facet below narrows the LOADED page and
  // nothing beyond it, which is why the caption keeps saying "of N loaded".

  /** Free-text over title / owner name / owner email. */
  searchQuery = "";
  /** "all" = every reason; otherwise one `mappingReason` key. */
  reasonFilter: MappingReasonFilter = "all";
  /** "" = every owner; otherwise an id from `ownerOptions`. */
  ownerFilter = "";

  /** Column sort over the filtered rows. Defaults to the order the service
   *  already returns, so the first paint is unchanged. */
  sortKey: MappingQueueSortKey = "createdAt";
  sortDir: MappingQueueSortDir = "desc";

  /** Rows the last successful queue load returned (null before the first one). */
  loadedRowCount: number | null = null;

  private unsubscribe: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** `meeting-mappings` — the queue itself (find) + the assign action (patch). */
  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("meeting-mappings") ?? this.rootStore.meetingMappingsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  /** The shared `clients` ServiceStore — the mapping target. */
  private get clientsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  /** `partners` — optional external parties recorded alongside the client. */
  private get partnersService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("partners") ?? this.rootStore.partnersStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /**
   * THE ROWS THE NEWEST SUCCESSFUL `find()` RETURNED — not the ServiceStore's
   * merge cache (C2). `meeting-mappings` is registered without `replaceOnFind`,
   * so `entities` accumulates: a meeting marked internal left the server's
   * `needs_review` list and STAYED ON THIS SCREEN, still offering Assign and
   * Internal buttons for a row that is no longer queued. A queue that renders a
   * union of every answer it has ever had is not a queue. Public so tests can
   * seed it without a round trip; only `load()` writes it in production.
   */
  queue: MeetingMappingRow[] = [];

  /**
   * The queue narrowed by the header facets — the two cheap enum narrows
   * first, then the free-text search LAST over whatever survives them.
   */
  get filteredQueue(): MeetingMappingRow[] {
    let result = this.queue;

    if (this.reasonFilter !== "all") {
      result = result.filter((row) => row.mappingReason === this.reasonFilter);
    }

    if (this.ownerFilter) {
      result = result.filter((row) => ownerKey(row) === this.ownerFilter);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (row) =>
          row.title?.toLowerCase().includes(q) ||
          row.ownerName?.toLowerCase().includes(q) ||
          row.ownerEmail?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: the FILTERED set under the active column sort.
   * Nulls sort last in either direction, so a row with no start time never
   * floats to the top of a desc sort.
   */
  get sortedQueue(): MeetingMappingRow[] {
    const key = this.sortKey;
    const valueOf = (row: MeetingMappingRow): SortValue => {
      switch (key) {
        case "title":
          return textValue(row.title);
        case "ownerName":
          return textValue(row.ownerName ?? row.ownerEmail);
        case "reason":
          // Sort by the label the operator READS, not by the wire key — the
          // two do not share an order.
          return textValue(this.reasonFor(row)?.label);
        case "startTime":
          return dateValue(row.startTime);
        case "createdAt":
          return dateValue(row.createdAt);
      }
    };
    return sortRows(this.filteredQueue, valueOf, this.sortDir);
  }

  /** Distinct owners present in the loaded queue, for the Owner filter. */
  get ownerOptions(): MappingQueueOwnerOption[] {
    const byKey = new Map<string, string>();
    for (const row of this.queue) {
      const id = ownerKey(row);
      if (id && !byKey.has(id)) {
        byKey.set(id, row.ownerName || row.ownerEmail || id);
      }
    }
    return [...byKey.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * True when a facet is narrowing the list. This is the whole difference
   * between "the queue is clear" and "your filters hide everything in it" —
   * an empty table that means the first when it means the second tells the
   * operator there is no work waiting when there is.
   */
  get hasActiveFilters(): boolean {
    return (
      this.searchQuery.trim() !== "" ||
      this.reasonFilter !== "all" ||
      this.ownerFilter !== ""
    );
  }

  /** Rows loaded, before the facets — the denominator of the count line. */
  get loadedCount(): number {
    return this.queue.length;
  }

  /**
   * The line above the table: "Showing N of M loaded unmapped meetings"
   * whenever the facets hide rows, plus the cap warning whenever the load came
   * back full. Both clauses matter together — filtering a capped page filters
   * the loaded rows only, so a filtered count must never read as a total.
   */
  get caption(): string | null {
    return listCaption(
      this.filteredQueue.length,
      this.loadedCount,
      this.loadCap,
      "unmapped meetings",
      // countsShown=false: this page has no summary cards, so the default
      // clause about "every count on this screen" would describe nothing.
      false
    );
  }

  /**
   * Which empty state the table must render when it has no rows to show.
   *
   *  - `filtered` — rows ARE loaded and the facets hide them. This wins over
   *    everything: loaded rows are proof the queue is not clear, whatever a
   *    later refresh did.
   *  - `error` — nothing is loaded AND THE QUEUE READ failed, so NOTHING IS
   *    KNOWN about what is waiting. Saying "every meeting is mapped 🎉" here
   *    reports a failure as a success, which is the worst thing this cell can
   *    do. Note it branches on {@link queueLoadFailed}, not on `error`: a
   *    refused assign leaves `error` set and tells us nothing about the queue.
   *  - `clear` — nothing is loaded and the load succeeded. Only now is the
   *    queue genuinely empty.
   */
  get emptyState(): "filtered" | "error" | "clear" {
    if (this.loadedCount > 0) return "filtered";
    if (this.queueLoadFailed) return "error";
    return "clear";
  }

  /** What the last queue load asked for vs. what came back (see ../loadCap.ts). */
  get loadCap(): LoadCap {
    return {
      requested: MAPPING_QUEUE_SERVER_CAP,
      returned: this.loadedRowCount,
    };
  }

  /** True when the queue came back at the service's hardcoded 200-row limit —
   *  there are more unmapped meetings than this screen can show. */
  get isTruncated(): boolean {
    return isAtCap(this.loadCap);
  }

  /** Client options for the picker, sorted by name. */
  get clients(): ClientRecord[] {
    return ((this.clientsService?.entities ?? []) as unknown as ClientRecord[])
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }

  /** Partner options for the optional external-parties picker. */
  get partners(): PartnerOption[] {
    return ((this.partnersService?.entities ?? []) as unknown as PartnerOption[])
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }

  /** Resolve a clientId to its display name (backs the candidate-client hints). */
  clientName(clientId: string): string {
    return this.clients.find((c) => c.id === clientId)?.name || clientId;
  }

  /** Reason metadata for a queued row (null when the API sent no reason). */
  reasonFor(
    row: MeetingMappingRow
  ): (typeof MAPPING_REASON_META)[MappingReason] | null {
    return row.mappingReason
      ? MAPPING_REASON_META[row.mappingReason] ?? null
      : null;
  }

  selectionFor(meetingId: number): Selection {
    return this.selections[meetingId] ?? { clientId: "", partnerIds: [] };
  }

  setClient(meetingId: number, clientId: string) {
    this.selections = {
      ...this.selections,
      [meetingId]: { ...this.selectionFor(meetingId), clientId },
    };
  }

  setPartners(meetingId: number, partnerIds: string[]) {
    this.selections = {
      ...this.selections,
      [meetingId]: { ...this.selectionFor(meetingId), partnerIds },
    };
  }

  // ---- Facet setters ------------------------------------------------------

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setReasonFilter(filter: MappingReasonFilter) {
    this.reasonFilter = filter;
  }

  setOwnerFilter(ownerId: string) {
    this.ownerFilter = ownerId;
  }

  /** Reset every facet — the way out of "nothing matches these filters". */
  clearFilters() {
    this.searchQuery = "";
    this.reasonFilter = "all";
    this.ownerFilter = "";
  }

  /** Column-header click: the same column toggles direction, a new column
   *  starts in its natural one (dates newest-first, text A→Z). */
  setSort(key: MappingQueueSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      // `meeting-mappings` reads no query and hardcodes `.limit(200)`, so there
      // is nothing to ask for — only the returned row count to notice. Coming
      // back at exactly the cap means more meetings are waiting than are shown.
      //
      // The clients/partners reads fill the SHARED picker caches, which this
      // store reads through `entities` because other screens fill them too.
      // They are deliberately not fatal to the queue: a failed clients load
      // leaves the last known options and must never make an empty table say
      // "we could not read the queue" — that claim belongs to the queue read
      // alone (see `queueLoadFailed`).
      const [queueResponse] = await Promise.all([
        this.service?.find({ query: {} }),
        this.clientsService?.find({ query: { $limit: 500, $sort: { name: 1 } } }),
        this.partnersService?.find({ query: { $limit: 500, $sort: { name: 1 } } }),
      ]);
      // C1. The registered ServiceStore runs with the default
      // `throwOnError = false`: a FAILED find records the reason on
      // `errorOnFind` and RESOLVES `null` rather than rejecting. Read as "no
      // rows", that rendered "Nothing waiting — every meeting is mapped" with
      // the API down. A null response is the same event as a rejection, and
      // carries the message the wrapper swallowed
      // (views/devops/shared/swallowedFind.ts).
      // The clients/partners reads swallow a sign-out into null, so the
      // Promise.all can still resolve with the queue read before it.
      if (ended()) return;
      if (queueResponse == null) {
        throw swallowedFailure(this.service, "errorOnFind", "Failed to load mapping queue");
      }
      const returned = rowsReturned(queueResponse);
      runInAction(() => {
        // C2. The response REPLACES the queue; it is not merged into it. A row
        // the newest answer omits — a meeting just marked internal or assigned
        // — leaves the screen, which is the whole meaning of a queue.
        this.queue = rowsOf<MeetingMappingRow>(
          queueResponse as unknown as
            | { data?: MeetingMappingRow[] }
            | MeetingMappingRow[]
        );
        this.queueLoadFailed = false;
        if (returned !== null) this.loadedRowCount = returned;
      });
    } catch (err: unknown) {
      // A sign-out is not a failed queue read: reset() cleared both fields.
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load mapping queue";
        this.queueLoadFailed = true;
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }
  async reload() {
    await this.load();
  }

  /**
   * Assign a queued meeting to the picked client (plus any external partners).
   * Contract: `patch(meetingId, { action: 'assign', clientId, partnerIds? })`.
   */
  async assign(meetingId: number): Promise<void> {
    const sel = this.selectionFor(meetingId);
    if (!sel.clientId) {
      this.error = "Pick a client to assign.";
      return;
    }
    this.assigningId = meetingId;
    this.error = null;
    try {
      const payload: MeetingMappingAssignPayload = {
        action: "assign",
        clientId: sel.clientId,
      };
      if (sel.partnerIds.length > 0) payload.partnerIds = sel.partnerIds;
      // C1. A REFUSED patch does not reject on this wrapper — it records
      // `errorOnPatch` and resolves `undefined`. Awaiting it and moving on
      // meant a forbidden or rejected assign set no error at all: the row
      // simply stayed put and the operator was told nothing. The service
      // returns `{ meetingId, clientId, … }` on success, so a nullish result
      // is a refusal, and the recorded reason is the message to show.
      const result = await this.service?.patch(String(meetingId), payload as never);
      if (result == null) {
        throw swallowedFailure(this.service, "errorOnPatch", "Failed to assign meeting");
      }
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Failed to assign meeting";
      });
    } finally {
      runInAction(() => {
        this.assigningId = null;
      });
    }
  }

  /**
   * Resolve a queued meeting as an IntraSync-internal meeting — no client, and
   * it leaves the queue. NOT "no extraction": HQ still runs the half of its
   * pipeline that needs no client (the `meeting_analysis` tasks), the deep
   * analyser still runs it, and the meeting is listed on Internal Meetings.
   * What it never gets is client attribution — no client memory, no
   * distribution, and no action items from HQ's own extraction. Contract:
   * `patch(meetingId, { action: 'mark-internal' })`.
   */
  async markInternal(meetingId: number): Promise<void> {
    this.assigningId = meetingId;
    this.error = null;
    try {
      const payload: MeetingMappingInternalPayload = { action: "mark-internal" };
      // C1, same wrapper, same swallow: `markInternal` returns
      // `{ meetingId, status: 'internal', analyzed }` on success, so a nullish
      // result is a refusal that resolved rather than threw.
      const result = await this.service?.patch(String(meetingId), payload as never);
      if (result == null) {
        throw swallowedFailure(this.service, "errorOnPatch", "Failed to mark meeting internal");
      }
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to mark meeting internal";
        }
      });
    } finally {
      runInAction(() => {
        this.assigningId = null;
      });
    }
  }

  // ---- Realtime -----------------------------------------------------------

  /**
   * Subscribe to the Meeting Intelligence live channel so the queue reflects
   * newly ingested meetings without a manual refresh — the same pattern the
   * Meetings list and Review Queue use.
   *
   * A meeting enters this queue the moment its transcript is ingested and the
   * auto-mapper fails to resolve a client, and can leave it when processing
   * completes, so both events reload. Idempotent: repeated calls are no-ops
   * until `dispose()`.
   *
   * AND THE THIRD EVENT (C7, 2026-09-03). A meeting also leaves this queue by
   * being marked internal, and nothing announced that: `meeting-mappings`
   * carried no events and `runPipeline`'s internal branch returns before
   * `emitProcessed`, so ANOTHER operator's queue kept offering Assign and
   * Internal on a row that had already been resolved. The API now emits
   * `meeting-marked-internal` on `meeting-mappings`, and this listens for it.
   * The operator who pressed the button still reloads from their own write —
   * the `assigningId` guard below is what stops those two racing.
   */
  subscribe() {
    if (this.unsubscribe) return;
    // Shared realtime bus (src/realtime) — auth is sourced internally from the
    // AuthStore, so a subscription can no longer be made "without a token" and
    // silently receive nothing.
    this.unsubscribe = subscribeMeetingIntelEvents(
      this.rootStore,
      (event: MeetingIntelEvent) => {
        if (
          event.kind !== "transcript-received" &&
          event.kind !== "meeting-processed" &&
          event.kind !== "meeting-marked-internal" &&
          // …and re-enters it when one is sent back from Internal Meetings.
          event.kind !== "meeting-unmarked-internal" &&
          // …and leaves it when someone maps it to a client (2026-11-02):
          // under Gantry authority the reprocess an assign runs announces
          // nothing else, so another operator kept being offered the row.
          event.kind !== "meeting-mapped"
        ) {
          return;
        }
        // An assign/mark-internal in flight reloads itself on completion —
        // don't race a second load against it.
        if (this.assigningId != null) return;
        void this.reload();
      }
    );
  }

  /** Tear down the realtime subscription (call on route exit). */
  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.loading = false;
    this.error = null;
    this.queueLoadFailed = false;
    this.assigningId = null;
    this.selections = {};
    this.searchQuery = "";
    this.reasonFilter = "all";
    this.ownerFilter = "";
    this.sortKey = "createdAt";
    this.sortDir = "desc";
    this.loadedRowCount = null;
    this.unsubscribe = null;
    this.queue = [];
  }
}

export default MappingQueueStore;
